import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
	addInsuranceProviderOptions,
	getInsuranceProviderOptions,
	InsuranceTypeOptions,
	PayorTypeOptions,
	editInsuranceProviderOptions
} from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { BuildingIcon } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { FormProps } from '~/shared/forms/Form';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	name: Yup.string().label('Insurance Provider Name').nullable().required(),
	address: Yup.object().label('Address').address().required(),
	insuranceTypeId: Yup.string().label('Insurance Type').nullable().required(),
	payorTypeId: Yup.string().label('Payor Type').nullable().required(),
	phoneNumber: Yup.string().label('Phone Number').nullable().required().phoneNumber(),
	faxNumber: Yup.string().label('Fax Number').nullable().phoneNumber(),
	emailAddress: Yup.string().label('Email Address').nullable().email(),
	notes: Yup.string().label('Notes').nullable(),
	nomncAppealPhoneNumber: Yup.string().label('NOMNC Appeal Phone Number').nullable().phoneNumber(),
	requiresNoticeOfAdmission: Yup.boolean().label('Requires Notice Of Admission').nullable(),
	requiresFaceToFaceDocumentation: Yup.boolean().label('Requires Face-to-Face Documentation').nullable(),
	requiresOasis: Yup.boolean().label('Requires OASIS').nullable(),
	companyAcceptsInsurance: Yup.boolean().label('Company Accepts Insurance').nullable()
});

export function EditInsuranceProviderForm() {
	const { id } = useParams<'id'>();
	const options = getInsuranceProviderOptions(id!);
	const { data: insuranceProvider, isLoading } = useSuspenseQuery(options);

	if (isLoading) {
		return <div>Loading...</div>;
	}

	return (
		<InsuranceProviderForm
			initialValues={{ ...insuranceProvider, insuranceProviderId: id }}
			saveMutation={{
				...editInsuranceProviderOptions,
				invalidateQueryKeys: [options.queryKey]
			}}
			title={`Edit Insurance Provider: ${insuranceProvider.name}`}
			description={`Make changes to the insurance provider ${insuranceProvider.name}`}
		/>
	);
}

export function AddInsuranceProviderForm() {
	const navigate = useNavigate();
	return (
		<InsuranceProviderForm
			initialValues={{
				name: null,
				insuranceTypeId: null,
				address: null,
				phoneNumber: null,
				faxNumber: null,
				emailAddress: null,
				notes: null,
				requiresNoticeOfAdmission: false,
				requiresFaceToFaceDocumentation: false,
				requiresOasis: false,
				requiresHomeboundStatus: false,
				payorTypeId: null,
				companyAcceptsInsurance: true,
				nomncAppealPhoneNumber: null
			}}
			saveMutation={{
				...addInsuranceProviderOptions,
				invalidateQueryKeys: [getSearchQueryKey('insurance-provider')]
			}}
			onSuccess={() => navigate('/settings/libraries/insuranceproviders')}
			title="Add Insurance Provider"
			description="Add an insurance provider to the system"
		/>
	);
}

function InsuranceProviderForm(props: Pick<FormProps, 'initialValues' | 'saveMutation' | 'onSuccess'> & { title: string; description: string }) {
	const navigate = useNavigate();
	return (
		<LibraryForm
			{...props}
			TitleIcon={BuildingIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to InsuranceProvider Library',
				errorAction: () => navigate('/settings/libraries/pharmacies')
			}}
		>
			<div className="grid grid-cols-2 gap-6">
				<div className="col-span-2">
					<TextFormField
						name="name"
						inputProps={{
							placeholder: 'Enter Insurance Provider Name'
						}}
						className="col-span-2 w-full"
					/>
				</div>
				<SelectFormField name="insuranceTypeId" label="Insurance Type" options={InsuranceTypeOptions} />
				<SelectFormField name="payorTypeId" label="Payor Type" options={PayorTypeOptions} />
				<PhoneNumberFormField name="phoneNumber" />
				<PhoneNumberFormField name="faxNumber" />
				<TextFormField name="emailAddress" className="col-span-2" />
				<CheckboxFormField name="companyAcceptsInsurance" hideLabel className="col-span-2" />
				<TextAreaFormField name="notes" className="col-span-2" />
				<div className="col-span-2">
					<AddressFormField name="address" />
				</div>
			</div>
		</LibraryForm>
	);
}
